// Client-side chart export for the Recharts line chart.
//
// Recharts renders the plot as an <svg class="recharts-surface"> but draws the
// custom legend (see LineChartWidget renderLegend) as a sibling HTML <div>
// (.recharts-legend-wrapper), OUTSIDE the surface svg. Serializing the surface
// alone would drop the legend, so we assemble a standalone svg that nests the
// surface and reconstructs the legend as svg — reading the already-rendered
// legend swatches and their real on-screen geometry so wrapped rows are
// reproduced exactly. No third-party dependencies.

const SVG_NS = 'http://www.w3.org/2000/svg';
const BACKGROUND = '#ffffff';
const LEGEND_TEXT_COLOR = '#000000';
const PNG_SCALE = 2; // rasterize at 2x for crisp output
// Self-contained font: a system stack so the exported file opens identically in
// Figma / Illustrator / Inkscape without the page's web font (README 3.1).
const FONT_STACK = 'system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';
const TITLE_SIZE = 16;
const SUBTITLE_SIZE = 12;
const HEADER_GAP = 8;
// Breathing room around the plot. Larger on the left so the rotated Y-axis
// label (positioned with a negative dx in LineChartWidget) isn't clipped.
const PADDING = { top: 8, right: 12, bottom: 8, left: 28 };

// Footer beneath the legend: "Source: <label>  ·  Data as of <date>", the page
// URL, then a wrapped disclaimer. Source + disclaimer come from the chart
// config; the URL and date are read from the page at download time.
const FOOTER_TOP_GAP = 16;
const FOOTER_SOURCE_SIZE = 13;
const FOOTER_SOURCE_GAP = 6;    // below the source line
const FOOTER_URL_SIZE = 11;
const FOOTER_URL_MARGIN = 0;    // above the URL
const FOOTER_URL_BOTTOM = 7;    // below the URL
const FOOTER_DISCLAIMER_SIZE = 11;
const FOOTER_DISCLAIMER_GAP = 3;    // between wrapped disclaimer lines
const FOOTER_DISCLAIMER_BOTTOM = 15;    // below the disclaimer
const FOOTER_GREEN = '#4d7b02';
const FOOTER_MUTED = '#666666';
const FOOTER_URL_COLOR = '#6f7379';
const FOOTER_DISCLAIMER_COLOR = '#8b919b';
const MONO_STACK = 'ui-monospace, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace';

function el(name, attrs = {}) {
  const node = document.createElementNS(SVG_NS, name);
  Object.entries(attrs).forEach(([k, v]) => node.setAttribute(k, v));
  return node;
}

// One reused offscreen 2d context to measure text width (disclaimer wrapping).
let measureCtx = null;
function textWidth(text, font) {
  if (!measureCtx) measureCtx = document.createElement('canvas').getContext('2d');
  measureCtx.font = font;
  return measureCtx.measureText(text).width;
}

// Greedily wrap text to fit maxWidth at the given CSS font. An unbreakable word
// overflows on its own line rather than being dropped.
function wrapText(text, maxWidth, font) {
  const words = String(text).split(/\s+/).filter(Boolean);
  const lines = [];
  let line = '';
  words.forEach(word => {
    const candidate = line ? `${line} ${word}` : word;
    if (line && textWidth(candidate, font) > maxWidth) {
      lines.push(line);
      line = word;
    } else {
      line = candidate;
    }
  });
  if (line) lines.push(line);
  return lines;
}

// Current page URL without query/hash — a clean canonical link for the footer.
function currentPageUrl() {
  const { origin, pathname } = window.location;
  return `${origin}${pathname}`;
}

// Today formatted like "July 23, 2026" to match the footer mock.
function todayLabel() {
  return new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

// Rebuild the legend as an svg <g>, positioned to match its rendered layout.
// Returns { group, height } — group is null when there is no legend.
function buildLegendGroup(legendWrapper) {
  const container = legendWrapper?.firstElementChild;
  if (!container) return { group: null, height: 0 };

  const items = Array.from(container.children);
  if (items.length === 0) return { group: null, height: 0 };

  const wrapRect = legendWrapper.getBoundingClientRect();
  const group = el('g');

  items.forEach(item => {
    const swatch = item.querySelector('svg');
    const label = item.querySelector('span');
    if (!swatch || !label) return;

    const itemRect = item.getBoundingClientRect();
    const swatchRect = swatch.getBoundingClientRect();
    const labelRect = label.getBoundingClientRect();

    // Positions relative to the legend wrapper's top-left origin.
    const swatchX = swatchRect.left - wrapRect.left;
    const swatchY = swatchRect.top - wrapRect.top;
    const labelX = labelRect.left - wrapRect.left;
    const labelCenterY = (labelRect.top + labelRect.bottom) / 2 - wrapRect.top;

    // The swatch is already an <svg> of svg primitives — nest it verbatim.
    const swatchClone = swatch.cloneNode(true);
    swatchClone.setAttribute('x', swatchX);
    swatchClone.setAttribute('y', swatchY);
    group.appendChild(swatchClone);

    const text = el('text', {
      x: labelX,
      y: labelCenterY,
      'dominant-baseline': 'central',
      'font-size': parseFloat(getComputedStyle(label).fontSize) || 14,
      fill: LEGEND_TEXT_COLOR,
    });
    text.textContent = label.textContent;
    group.appendChild(text);
  });

  return { group, height: wrapRect.height };
}

// Assemble a standalone, self-contained svg element from a rendered
// .recharts-wrapper. Returns null when no chart surface is present.
// Title + subtitle drawn at the top of the exported image (README 1.1).
// Returns { nodes, height }; height is 0 when neither is present.
function buildHeaderText({ title, subtitle, x, top }) {
  const nodes = [];
  let y = top;
  if (title) {
    y += TITLE_SIZE;
    const node = el('text', { x, y, 'font-size': TITLE_SIZE, 'font-weight': 700, fill: '#111111' });
    node.textContent = title;
    nodes.push(node);
    y += HEADER_GAP;
  }
  if (subtitle) {
    y += SUBTITLE_SIZE;
    const node = el('text', { x, y, 'font-size': SUBTITLE_SIZE, fill: '#666666' });
    node.textContent = subtitle;
    nodes.push(node);
    y += HEADER_GAP;
  }
  return { nodes, height: nodes.length ? y - top : 0 };
}

// Footer drawn beneath the legend: source line, page URL, wrapped disclaimer.
// Returns { nodes, height }; height 0 when there is nothing to draw. `maxWidth`
// bounds the disclaimer wrap.
function buildFooter({ source, url, dataAsOf, disclaimer, x, top, maxWidth }) {
  const nodes = [];
  let y = top;

  if (source || dataAsOf) {
    y += FOOTER_TOP_GAP + FOOTER_SOURCE_SIZE;
    const line = el('text', { x, y, 'font-size': FOOTER_SOURCE_SIZE });
    if (source) {
      const strong = el('tspan', { fill: FOOTER_GREEN, 'font-weight': 700 });
      strong.textContent = `Source: ${source}`;
      line.appendChild(strong);
    }
    if (dataAsOf) {
      const rest = el('tspan', { fill: FOOTER_MUTED });
      rest.textContent = `${source ? '    ·    ' : ''}Data as of ${dataAsOf}`;
      line.appendChild(rest);
    }
    nodes.push(line);
    y += FOOTER_SOURCE_GAP;
  }

  if (url) {
    y += FOOTER_URL_MARGIN + FOOTER_URL_SIZE;
    const node = el('text', { x, y, 'font-size': FOOTER_URL_SIZE, fill: FOOTER_URL_COLOR });
    node.textContent = url;
    nodes.push(node);
    y += FOOTER_URL_BOTTOM;
  }

  if (disclaimer) {
    const font = `${FOOTER_DISCLAIMER_SIZE}px ${MONO_STACK}`;
    wrapText(disclaimer, maxWidth, font).forEach((lineText, i) => {
      if (i > 0) y += FOOTER_DISCLAIMER_GAP;
      y += FOOTER_DISCLAIMER_SIZE;
      const node = el('text', {
        x, y,
        'font-size': FOOTER_DISCLAIMER_SIZE,
        'font-family': MONO_STACK,
        fill: FOOTER_DISCLAIMER_COLOR,
      });
      node.textContent = lineText;
      nodes.push(node);
    });
    y += FOOTER_DISCLAIMER_BOTTOM;
  }

  return { nodes, height: nodes.length ? y - top : 0 };
}

function buildStandaloneChartSvg(wrapperEl, { title, subtitle, footer } = {}) {
  const surface = wrapperEl?.querySelector('.recharts-surface');
  if (!surface) return null;

  const surfaceRect = surface.getBoundingClientRect();
  const width = parseFloat(surface.getAttribute('width')) || surfaceRect.width;
  const surfaceHeight = parseFloat(surface.getAttribute('height')) || surfaceRect.height;

  const { group: legendGroup, height: legendHeight } =
    buildLegendGroup(wrapperEl.querySelector('.recharts-legend-wrapper'));

  const totalWidth = width + PADDING.left + PADDING.right;
  const header = buildHeaderText({ title, subtitle, x: PADDING.left, top: PADDING.top });
  const surfaceY = PADDING.top + header.height;

  const footerBuilt = footer
    ? buildFooter({
      source: footer.source,
      disclaimer: footer.disclaimer,
      url: currentPageUrl(),
      dataAsOf: todayLabel(),
      x: PADDING.left,
      top: surfaceY + surfaceHeight + legendHeight,
      maxWidth: width,
    })
    : { nodes: [], height: 0 };

  const totalHeight = surfaceY + surfaceHeight + legendHeight + footerBuilt.height + PADDING.bottom;

  const svg = el('svg', {
    xmlns: SVG_NS,
    width: totalWidth,
    height: totalHeight,
    viewBox: `0 0 ${totalWidth} ${totalHeight}`,
    'font-family': FONT_STACK,
  });

  svg.appendChild(el('rect', { x: 0, y: 0, width: totalWidth, height: totalHeight, fill: BACKGROUND }));
  header.nodes.forEach(node => svg.appendChild(node));

  const surfaceClone = surface.cloneNode(true);
  surfaceClone.setAttribute('x', PADDING.left);
  surfaceClone.setAttribute('y', surfaceY);
  // Serialized svg has no CSS, so nested viewports default to clipping — let
  // axis labels that extend past the surface bounds render into the padding.
  surfaceClone.setAttribute('overflow', 'visible');
  svg.appendChild(surfaceClone);

  if (legendGroup) {
    legendGroup.setAttribute('transform', `translate(${PADDING.left}, ${surfaceY + surfaceHeight})`);
    svg.appendChild(legendGroup);
  }

  footerBuilt.nodes.forEach(node => svg.appendChild(node));

  return svg;
}

function serialize(svg) {
  return new XMLSerializer().serializeToString(svg);
}

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function downloadSvg(svg, filename) {
  const blob = new Blob([serialize(svg)], { type: 'image/svg+xml;charset=utf-8' });
  triggerDownload(blob, filename);
}

function downloadPng(svg, filename) {
  const width = parseFloat(svg.getAttribute('width'));
  const height = parseFloat(svg.getAttribute('height'));
  // Use a data: URL (not blob:) as the image source — the site's CSP allows
  // `img-src data:` but not `blob:`. encodeURIComponent keeps UTF-8 labels intact.
  const source = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(serialize(svg))}`;
  const image = new Image();

  image.onload = () => {
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(width * PNG_SCALE);
    canvas.height = Math.round(height * PNG_SCALE);
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = BACKGROUND;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.scale(PNG_SCALE, PNG_SCALE);
    ctx.drawImage(image, 0, 0, width, height);
    canvas.toBlob(pngBlob => pngBlob && triggerDownload(pngBlob, filename), 'image/png');
  };
  image.src = source;
}

// Download an in-memory string as a file (e.g. client-generated CSV of the
// current selection).
export function downloadTextFile(text, filename, mime = 'text/plain;charset=utf-8') {
  triggerDownload(new Blob([text], { type: mime }), filename);
}

export function slugifyFilename(value, fallback = 'chart') {
  const slug = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug || fallback;
}

// Public entry point. `wrapperEl` is a rendered .recharts-wrapper (or an
// ancestor of it). Returns false when there is no chart to export.
export function downloadChartImage(wrapperEl, { format, filename, title, subtitle, footer } = {}) {
  const svg = buildStandaloneChartSvg(wrapperEl, { title, subtitle, footer });
  if (!svg) return false;

  if (format === 'svg') {
    downloadSvg(svg, `${filename}.svg`);
  } else {
    downloadPng(svg, `${filename}.png`);
  }
  return true;
}
